// ================================================================
// Shared portfolio maths + money formatting for the Portfolio tab.
// Used by PortfolioReport (report view) and PortfolioRevenue
// (Revenue & return, shown in the Performance tab).
//
// Reporting currencies are EUR (primary) and AED. Everything stored
// in the app is AED, so every helper takes AED and converts.
// ================================================================
import { useEffect, useState } from 'react';
import { portalFetch } from '../portals/shared/api';
import { appLocale, appLanguage, monthNames, trMilestone } from '../i18n/locale';

// 1 AED ≈ €0.25 — the same rate the rest of the app uses (EUR_RATE 0.25).
export const AED_PER_EUR = 4.0;
export const NET_RENT_RATIO = 0.85;        // net = gross − service charges/OPEX when no actuals exist
export const NEW_CAPITAL_YIELD = 0.075;    // assumed net yield on newly deployed capital
export const YIELD_STEP = 0.005;           // yield improvement per year in the long-term projection
export const YIELD_CEILING = 0.10;
export const EUR_LADDER = [1e6, 5e6, 10e6, 25e6, 50e6, 100e6, 250e6, 500e6, 1e9];
export const RENT_LADDER = [1e5, 5e5, 1e6, 3e6, 5e6, 7e6, 10e6, 25e6];

// ---------------------------------------------------------------- types
export interface Property {
  id: string;
  name?: string;
  project?: string;
  location?: string;
  unitNumber?: string;
  area?: string | number;
  bedrooms?: number | string;
  type?: string;
  purchaseValue?: number;
  currentValue?: number;
  marketValue?: number;
  monthlyRental?: number;
  purchaseDate?: string;
  otpDate?: string;
  createdAt?: string;
  completionDate?: string;
  estimatedCompletionDate?: string;
  occupancyStatus?: string;
  developer?: string;
  projectProgress?: number;
  /** Live from the DLD construction feed; wins over occupancyStatus. */
  constructionCompletionPercent?: number | null;
  isOffplan?: boolean;
  appreciationAmount?: number;
  /** 'active' | 'rented' | 'selling' | 'sold' — anything sold leaves the active portfolio. */
  status?: string;
  // ---- realised sale, written by /api/units/:id/sale (or the DubaiSell webhook)
  salePrice?: number;
  saleDate?: string;
  soldAt?: string;
  saleCosts?: SaleCosts;
  saleTotalCosts?: number;
  netProceeds?: number;
  grossProfit?: number;
  netProfit?: number;
  roiPercent?: number;
  holdingMonths?: number | null;
  annualizedRoi?: number | null;
  saleRecordedBy?: string;
  saleNotes?: string;
  /** 'assignment' when the unit was sold off-plan, before handover */
  saleType?: SaleType;
  /** assignment only: how the unit was acquired ('developer' | 'assigned-in') */
  acquisitionRoute?: AcquisitionRoute;
  /** assignment only: price paid to a previous owner to take the SPA over */
  acquisitionPrice?: number;
  /** assignment only: developer balance that came with the SPA when it was taken over */
  assumedBalance?: number;
  /** assignment only: installments this owner paid to the developer */
  paidToDate?: number;
  /** assignment only: balance the buyer took over */
  outstandingBalance?: number;
  /** what acquiring the unit cost on top of the price, as stored by the backend */
  purchaseCosts?: {
    purchaseDldFee?: number;
    purchaseCommission?: number;
    purchaseTrusteeFee?: number;
    purchaseOtherCosts?: number;
  };
  cashToSeller?: number;
  capitalBase?: number;
}

export interface Milestone {
  installmentNumber: number;
  description: string;
  percentage: number;
  amount: number;
  dueDate: string;
  status: 'paid' | 'pending' | 'overdue';
}

export interface InstallmentPlan {
  id: string;
  unitId: string;
  unitName: string;
  project: string;
  unitNumber: string;
  totalPurchasePrice: number;
  milestones: Milestone[];
}

/** Lease facts pulled from the Property Management module for one unit. */
export interface LeaseInfo {
  annualRent: number;
  expenses: number;
  collected: number;
  tenant?: string;
  status?: string;
}

export interface PortfolioRow {
  unit: Property;
  dld: any;
  lease?: LeaseInfo;
  purchase: number;
  marketValue: number;
  /** true when marketValue came from DLD comparables, false when it is the purchase price */
  hasDldValue: boolean;
  offplan: boolean;
  rented: boolean;
  monthlyRent: number;
  estMonthlyRent: number;
  annualRent: number;
  handoverISO?: string;
  handoverLabel: string;
  area: number;
}

// ---------------------------------------------------------------- money
export const eur = (aed: number) => aed / AED_PER_EUR;

export const fmtEUR = (amount: number, decimals = 0) =>
  `€${amount.toLocaleString('de-DE', { minimumFractionDigits: decimals, maximumFractionDigits: decimals })}`;

/** trims "10.0" → "10", keeps "8.72" */
export const trimZeros = (value: string) =>
  value.replace(/\.0+$/, '').replace(/(\.\d*[1-9])0+$/, '$1');

/** €38.8m · €197.8k · €1,204 */
export const compactEUR = (amount: number): string => {
  const abs = Math.abs(amount);
  if (abs >= 1e6) return `€${trimZeros((amount / 1e6).toFixed(abs >= 100e6 ? 0 : 1))}m`;
  if (abs >= 1e5) return `€${Math.round(amount / 1e3)}k`;
  if (abs >= 1e4) return `€${trimZeros((amount / 1e3).toFixed(1))}k`;
  return fmtEUR(Math.round(amount));
};

export const compactAED = (aed: number): string => {
  const abs = Math.abs(aed);
  if (abs >= 1e6) return `AED ${trimZeros((aed / 1e6).toFixed(abs >= 100e6 ? 1 : 2))}m`;
  if (abs >= 1e4) return `AED ${Math.round(aed / 1e3)}k`;
  return `AED ${Math.round(aed).toLocaleString('en-AE')}`;
};

export const fmtAEDFull = (aed: number) => Math.round(aed).toLocaleString('en-AE');

export const pct = (value: number, digits = 1) => `${value.toFixed(digits)}%`;

// ---------------------------------------------------------------- area
const SQFT_PER_SQM = 10.764;

/**
 * Unit areas are stored inconsistently — some records hold sqm (e.g. 122.47),
 * others sqft (e.g. 1318). Same heuristic the backend uses (dldService.toSqft):
 * anything under 300 is treated as sqm. ALWAYS run raw area through this before
 * showing it or dividing by it, or a 122.47 m² flat reads as "122 sqft".
 */
export const toSqft = (area?: string | number): number => {
  const val = typeof area === 'string' ? parseFloat(area) || 0 : area || 0;
  if (val > 0 && val < 300) return Math.round(val * SQFT_PER_SQM);
  return val;
};

/** "1,318 sqft · 122.5 m²" */
export const formatArea = (area?: string | number): string => {
  const sqft = toSqft(area);
  if (!sqft) return '—';
  return `${Math.round(sqft).toLocaleString('en-AE')} sqft · ${(sqft / SQFT_PER_SQM).toFixed(1)} m²`;
};

export const nextLadder = (value: number, ladder: number[]) =>
  ladder.find(step => step > value * 1.02) || Math.ceil(value / 1e6) * 1e6;

// ---------------------------------------------------------------- dates
/** Month names in the app language (src/i18n/locale.ts) — call it, do not cache it. */
export const monthName = (index: number): string => monthNames()[index];

export const quarterLabel = (iso?: string): string => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '—';
  if (d.getTime() < Date.now()) return '—';
  return `Q${Math.floor(d.getMonth() / 3) + 1} ${d.getFullYear()}`;
};

export const dateLabel = (iso?: string) => {
  if (!iso) return '—';
  const d = new Date(iso);
  return isNaN(d.getTime())
    ? '—'
    : d.toLocaleDateString(appLocale(), { day: '2-digit', month: 'short', year: 'numeric' });
};

/** "3rd · 10%" — but never "20% · 20%" when the description already carries the rate */
export const rateLabel = (m: { description?: string; percentage?: number; installmentNumber: number }) => {
  const desc = m.description ? trMilestone(m.description) : (appLanguage() === 'de' ? `Rate ${m.installmentNumber}` : `Installment ${m.installmentNumber}`);
  return m.percentage && !desc.includes('%') ? `${desc} · ${m.percentage}%` : desc;
};

// ---------------------------------------------------------------- leases
/**
 * Signed leases from the Property Management module — real rent and real
 * expenses beat estimates. Best effort: if PM is dormant or unreachable the
 * caller falls back to unit.monthlyRental / DLD comparables.
 */
// A cheque counts as collected once it's marked paid/approved OR its due date
// has arrived (post-dated cheques are counted from their date onward).
export const chequeCollected = (r: any): boolean =>
  r?.status === 'paid' || r?.status === 'approved' ||
  (!!r?.dueDate && new Date(r.dueDate).getTime() <= Date.now());

/** Build the per-unit lease map from a /pm/owner/:id/units payload. */
function toLeaseMap(data: { units?: any[] } | null): Record<string, LeaseInfo> {
  const map: Record<string, LeaseInfo> = {};
  (data?.units || []).forEach((u: any) => {
    const schedule: any[] = u.rentSchedule || [];
    const scheduled = schedule.reduce((s, r) => s + (r.amount || 0), 0);
    // Only an actual signed tenant lease counts as rental income. Leftover
    // cheque schedules on unleased units must NOT inflate the portfolio's rent.
    if (!u.currentTenant) return;
    const annualRent = Number(u.currentTenant.annualRent) || scheduled;
    if (!annualRent) return;
    map[u.id] = {
      annualRent,
      expenses: (u.expenses || []).reduce((s: number, e: any) => s + (e.amount || 0), 0),
      collected: schedule
        .filter(chequeCollected)
        .reduce((s, r) => s + (r.amount || 0), 0),
      tenant: u.currentTenant?.name,
      status: u.status
    };
  });
  return map;
}

/**
 * One in-flight lease request per owner, shared by every hook instance.
 *
 * Three components call useLeases with the same investorId — PortfolioReport
 * and PortfolioRevenue both render in the Report tab, PortfolioCalculator in
 * the Calculator tab — so opening the Portfolio fired the same
 * /pm/owner/:id/units request two or three times, each with its own 4s timeout.
 * They now share one promise and one parsed result.
 *
 * The entry is kept after it resolves so a tab switch (which remounts the
 * hook) reuses it instead of refetching. It is dropped on failure, so a
 * request that failed because PM was briefly unreachable is retried by the
 * next mount rather than caching the failure for the session.
 */
const _pmUnitRequests = new Map<string, Promise<any[]>>();

/**
 * The RAW Property Management units, cached per owner.
 *
 * The cache holds the payload rather than the derived lease map, because two
 * different readings are taken from the same request: the per-unit lease
 * totals (useLeases) and the cheque-by-cheque rent calendar the Home revenue
 * trend needs (monthlyRentSeries). Deriving both from one cached promise keeps
 * it at one request per owner per session.
 */
function fetchPmUnits(investorId: string): Promise<any[]> {
  const existing = _pmUnitRequests.get(investorId);
  if (existing) return existing;
  const request = portalFetch<{ units: any[] }>(`/pm/owner/${encodeURIComponent(investorId)}/units`)
    .then(data => data?.units || [])
    .catch(err => {
      _pmUnitRequests.delete(investorId);
      throw err;
    });
  _pmUnitRequests.set(investorId, request);
  return request;
}

function fetchLeases(investorId: string): Promise<Record<string, LeaseInfo>> {
  return fetchPmUnits(investorId).then(units => toLeaseMap({ units }));
}

/**
 * Signed leases per unit, from Property Management.
 *
 * `override` is for the code-gated showcase (portals/demo): rental income is
 * taken ONLY from a signed lease — never a market estimate — so with the PM
 * call blocked in demo mode the report showed "no rented units yet" next to a
 * unit plainly marked Occupied. Passing the sample lease map fixes that
 * without weakening the rule for real accounts.
 */
export function useLeases(
  investorId?: string,
  override?: Record<string, LeaseInfo>,
): Record<string, LeaseInfo> {
  const [leases, setLeases] = useState<Record<string, LeaseInfo>>(override || {});

  useEffect(() => {
    if (override) return;   // showcase: nothing to fetch
    if (!investorId) return;
    let alive = true;
    fetchLeases(investorId)
      .then(map => { if (alive) setLeases(map); })
      .catch(() => { /* PM module off or offline — estimates stay in place */ });
    return () => { alive = false; };
  }, [investorId, override]);

  return override || leases;
}

/**
 * The raw Property Management units for this owner — cheque schedules and all.
 *
 * Same contract as useLeases: `override` is the showcase/film path (the PM
 * call is blocked there), and a failure leaves the array empty rather than
 * throwing, so a caller renders its empty state instead of breaking.
 */
export function usePmUnits(investorId?: string, override?: any[]): any[] {
  const [units, setUnits] = useState<any[]>(override || []);

  useEffect(() => {
    if (override) return;      // showcase: nothing to fetch
    if (!investorId) return;
    let alive = true;
    fetchPmUnits(investorId)
      .then(u => { if (alive) setUnits(u); })
      .catch(() => { /* PM module off or offline — the caller shows its empty state */ });
    return () => { alive = false; };
  }, [investorId, override]);

  return override || units;
}

// ---------------------------------------------------------------- rent trend
export interface RentMonth {
  /** 'YYYY-MM', for React keys and testing. */
  key: string;
  /** Short month label in the requested locale, e.g. 'Mar'. */
  label: string;
  /** Rent banked in this month alone, AED. */
  amount: number;
  /** Rent banked from the start of the window through this month, AED. */
  cumulative: number;
}

/**
 * Rent collected per month across the portfolio, for the last `months` months.
 *
 * WHY CUMULATIVE IS ALSO RETURNED. Dubai rent does not arrive monthly — a
 * tenancy is paid in one to four cheques a year. Plotted as monthly bars a
 * real portfolio is two spikes and ten empty columns, which reads as a broken
 * chart rather than as a healthy tenancy. The running total is the honest
 * shape: it rises in steps on the days cheques are actually banked.
 *
 * "Collected" is chequeCollected — marked paid/approved, OR past its due date,
 * since post-dated cheques are banked on the date they carry.
 */
export function monthlyRentSeries(
  units: any[],
  months = 12,
  locale = 'en-GB',
  now: Date = new Date(),
): RentMonth[] {
  // Window = the last `months` months INCLUDING the current one.
  const start = new Date(now.getFullYear(), now.getMonth() - (months - 1), 1);
  const buckets = new Map<string, number>();
  const order: { key: string; label: string }[] = [];

  for (let i = 0; i < months; i++) {
    const d = new Date(start.getFullYear(), start.getMonth() + i, 1);
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    buckets.set(key, 0);
    order.push({ key, label: d.toLocaleDateString(locale, { month: 'short' }) });
  }

  (units || []).forEach((u: any) => {
    // Only a signed tenancy counts, exactly as in toLeaseMap: a leftover
    // cheque schedule on an unleased unit must not show up as income.
    if (!u?.currentTenant) return;
    (u.rentSchedule || []).forEach((r: any) => {
      if (!chequeCollected(r)) return;
      const t = r?.dueDate ? new Date(r.dueDate) : null;
      if (!t || isNaN(t.getTime())) return;
      const key = `${t.getFullYear()}-${String(t.getMonth() + 1).padStart(2, '0')}`;
      if (!buckets.has(key)) return;       // outside the window
      buckets.set(key, (buckets.get(key) || 0) + (Number(r.amount) || 0));
    });
  });

  let running = 0;
  return order.map(({ key, label }) => {
    const amount = buckets.get(key) || 0;
    running += amount;
    return { key, label, amount, cumulative: running };
  });
}

// ------------------------------------------------------------- payouts
export interface NextPayout {
  amount: number;
  /** ISO / raw date string as the cheque carries it. */
  dueDate: string;
  unitName: string;
  /** Already banked — the cheque cleared, money is with us. */
  credited: boolean;
  /** Cheques banked so far / cheques in the schedule, across the portfolio. */
  collected: number;
  total: number;
  /** The cheque after this one, across the portfolio (owner request 2026-11-05). */
  following: { amount: number; dueDate: string; unitName: string } | null;
}

/**
 * The next rent cheque due across the portfolio, and how far through the
 * cheque run the year is.
 *
 * "Next" is the earliest cheque that has NOT been banked. When every cheque
 * has been banked there is nothing to show, so this returns null rather than
 * repeating the last one — a card headed "next payout" showing a date in the
 * past is worse than no card.
 *
 * NOTE ON THE BANK LINE in the reference design: the owner's payout account is
 * not stored anywhere in this product (the only `bankAccount` in the codebase
 * is a chatbot article about how to OPEN one), so there is no masked IBAN to
 * show and none is invented here.
 */
export function nextPayout(units: any[], now: Date = new Date()): NextPayout | null {
  const cheques: { amount: number; dueDate: string; unitName: string; t: number; banked: boolean }[] = [];

  (units || []).forEach((u: any) => {
    if (!u?.currentTenant) return;          // only a signed tenancy pays rent
    (u.rentSchedule || []).forEach((r: any) => {
      const amount = Number(r.amount) || 0;
      if (!amount) return;
      const t = r.dueDate ? Date.parse(r.dueDate) : NaN;
      cheques.push({
        amount,
        dueDate: r.dueDate || '',
        unitName: u.name || u.project || '',
        t: isNaN(t) ? Infinity : t,
        banked: chequeCollected(r),
      });
    });
  });

  if (cheques.length === 0) return null;

  const collected = cheques.filter((c) => c.banked).length;
  const pending = cheques.filter((c) => !c.banked).sort((a, b) => a.t - b.t);
  if (pending.length === 0) return null;

  const n = pending[0];
  return {
    amount: n.amount,
    dueDate: n.dueDate,
    unitName: n.unitName,
    // A cheque whose date has arrived counts as banked (chequeCollected), so
    // anything still pending is genuinely ahead of us.
    credited: false,
    collected,
    total: cheques.length,
    following: pending[1] ? { amount: pending[1].amount, dueDate: pending[1].dueDate, unitName: pending[1].unitName } : null,
  };
}

export interface LeaseRow {
  unitId: string;
  name: string;
  tenant: string | null;
  /** ISO day strings as the tenancy carries them. */
  start: string;
  end: string;
  /** Whole months the contract runs, rounded. */
  months: number;
  /** Contract rent for the lease. */
  rent: number;
  /** Rent actually banked from this lease so far. */
  collected: number;
  chequesBanked: number;
  chequesTotal: number;
  /** Costs booked against the unit during the lease. */
  booked: number;
  /** One year of the resolved service charge; null when no rate is known. */
  serviceCharge: number | null;
  /** Rent minus booked costs minus service charge. */
  net: number;
  purchasePrice: number;
  /** Net as a % of purchase price, per year of lease. null when no price. */
  netYield: number | null;
  /** Share of the lease term already passed, 0..1. */
  elapsed: number;
  daysLeft: number;
}

/**
 * The rental book, PER LEASE — the way an owner reads a let portfolio: each
 * signed tenancy with its term, its rent, what it costs and what it nets.
 *
 * Figures are the CONTRACT's, not split by day or by calendar year. Rent paid
 * a year ahead in one cheque made every period-based view (banked-by-year,
 * earned-by-day) misleading: a lease starting 27 December put either the
 * whole rent or five days of it into a year the tenancy barely touched. A
 * lease is the unit the rent is agreed in, so it is the unit shown.
 *
 * Costs per lease are the expenses booked against the unit within the lease
 * term, plus ONE YEAR of the backend's resolved service charge
 * (facts.netIncome.serviceCharge — already zero when the invoice is booked
 * or the building has not handed over), the same annual figure the unit's
 * own screen deducts, so the two always agree. A building with no published
 * rate reports `serviceCharge: null`, and the caller says so rather than
 * implying the net is complete.
 *
 * Only units let NOW (currentTenant) are leases here. Their cheque schedule
 * is filtered to the contract term, so a previous tenancy's cheques are not
 * credited to the current tenant.
 */
export function leaseBook(units: any[], now: Date = new Date()) {
  const DAY = 86400000;
  const nowMs = now.getTime();
  // A bare 'YYYY-MM-DD' is a local calendar day — Date.parse would read it as
  // UTC midnight and shift it by the timezone offset.
  const ms = (raw: any) => {
    const d = typeof raw === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(raw)
      ? new Date(+raw.slice(0, 4), +raw.slice(5, 7) - 1, +raw.slice(8, 10)).getTime()
      : raw ? Date.parse(raw) : NaN;
    return isNaN(d) ? null : d;
  };

  const rows: LeaseRow[] = [];
  (units || []).forEach((u: any) => {
    const tn = u?.currentTenant;
    if (!tn) return;
    const s = ms(tn.contractStart);
    const e = ms(tn.contractEnd);
    const inTerm = (t: number | null) =>
      t !== null && (s === null || t >= s - DAY) && (e === null || t <= e + DAY);

    const cheques = (u.rentSchedule || []).filter((r: any) => inTerm(ms(r?.dueDate)));
    const banked = cheques.filter((r: any) => chequeCollected(r));
    const termDays = s !== null && e !== null && e > s ? (e - s) / DAY + 1 : 365;
    // The rent for the WHOLE lease. annualRent is that only for a one-year
    // lease: a 5-year office lease (JBC 5 1101, 915,584 a year) read as
    // 915,584 for five years and showed a 1.7% net yield instead of ~8.9%.
    // Multi-year: the contract total the lease form saves, else annual rent x
    // years. Leases under two years keep the contract-figure reading.
    const annual = Number(tn.annualRent) || 0;
    const multiYear = termDays >= 700;
    const rent = (multiYear && Number(tn.contractValue) > 0 ? Number(tn.contractValue) : 0)
      || (multiYear && annual ? annual * (termDays / 365) : 0)
      || annual
      || cheques.reduce((a: number, r: any) => a + (Number(r.amount) || 0), 0);
    if (!rent) return;

    const booked = (u.expenses || [])
      .filter((x: any) => inTerm(ms(x?.date)))
      .reduce((a: number, x: any) => a + (Number(x.amount) || 0), 0);
    const ni = u.facts?.netIncome;
    const sc = Number(ni?.serviceCharge) || 0;
    // Known = a charge is deducted, or the backend says there is none due
    // (invoice already booked / not handed over).
    // One year of service charge per year of the lease (it is an annual charge).
    const serviceCharge = sc > 0 ? sc * (multiYear ? termDays / 365 : 1) : ni?.serviceChargeKnown ? 0 : null;
    const net = rent - booked - (serviceCharge || 0);

    // Yield on TOTAL investment where the purchase costs are on record (price +
    // DLD, agency and trustee fees, excl. VAT — units/{id}.purchaseCostsExVat,
    // from the transfer sheets): the owner's net yield = (rent - service
    // charge) / all costs. One rule for Home, Rental profit and Total wealth, so
    // the same portfolio can never show 8.8% on one card and 9.1% on the next
    // (2026-11-05). A unit without recorded costs stays on its price.
    // Purchase costs from the backend's facts.acquisition (recorded rows, else
    // the transfer-sheet total, else DLD + trustee fee by DLD rule) — the
    // Purchase tab of the unit shows the same rows (owner, 2026-11-05: one
    // rule for every investor).
    const acq = u.facts?.acquisition;
    const price = (Number(u.purchaseValue) || Number(u.purchasePrice) || 0)
      + (acq && Number.isFinite(Number(acq.total)) ? Number(acq.total) : (Number(u.purchaseCostsExVat) || 0));
    rows.push({
      unitId: u.id,
      name: u.name || u.project || '',
      tenant: tn.name || null,
      start: tn.contractStart || '',
      end: tn.contractEnd || '',
      months: Math.round(termDays / 30.44),
      rent,
      collected: banked.reduce((a: number, r: any) => a + (Number(r.amount) || 0), 0),
      chequesBanked: banked.length,
      chequesTotal: cheques.length,
      booked,
      serviceCharge,
      net,
      purchasePrice: price,
      netYield: price > 0 ? (net / price) * (365 / termDays) * 100 : null,
      elapsed: s !== null && e !== null && e > s
        ? Math.min(1, Math.max(0, (nowMs - s) / (e - s))) : 0,
      daysLeft: e !== null ? Math.max(0, Math.ceil((e - nowMs) / DAY)) : 0,
    });
  });

  // Soonest to end first: the lease that needs a renewal decision leads.
  rows.sort((a, b) => a.daysLeft - b.daysLeft);

  const sum = (k: keyof LeaseRow) => rows.reduce((a, r) => a + (Number(r[k]) || 0), 0);
  const rent = sum('rent');
  const costs = sum('booked') + sum('serviceCharge');
  const net = rent - costs;
  // Annualised per lease, then weighted by price — a 14-month lease must not
  // read as a higher yield just for being longer.
  const priced = rows.filter((r) => r.purchasePrice > 0 && r.netYield !== null);
  const pricedBasis = priced.reduce((a, r) => a + r.purchasePrice, 0);
  return {
    rows,
    rent,
    collected: sum('collected'),
    costs,
    net,
    /** Price-weighted annual net yield across priced leases. null when none. */
    netYield: pricedBasis > 0
      ? priced.reduce((a, r) => a + (r.netYield as number) * r.purchasePrice, 0) / pricedBasis
      : null,
    /** Leases whose building has no known service charge. */
    scUnknown: rows.filter((r) => r.serviceCharge === null).length,
    letUnits: rows.length,
    totalUnits: (units || []).length,
  };
}

// ------------------------------------------------------- portfolio value
export interface ValuePoint {
  /** ISO day, for React keys. */
  key: string;
  /** Short label, e.g. "Feb 22". */
  label: string;
  /** Portfolio value in AED at this point. */
  value: number;
  /** True for the final point — today's valuation rather than a purchase. */
  today: boolean;
}

/**
 * What the portfolio has been worth, as far as that can honestly be drawn.
 *
 * THERE IS NO VALUE HISTORY. The Dubai Land Department data behind this app
 * returns a valuation for TODAY — an estimate from recent comparables — not a
 * series. So a smooth rising curve of "your portfolio over time" would be a
 * drawing, not a record, on a screen whose whole claim is that the numbers
 * come from DLD transactions rather than from our opinion.
 *
 * What IS on record is every purchase: its date and its price. So the line is
 * built from those — it steps up on the day each unit was actually bought, to
 * the total paid so far — and the LAST point is today's market value. Every
 * vertex is a dated fact. The straight segments between them are the ordinary
 * way of joining known points, and claim nothing about the months in between;
 * `today` marks the final point so the caller can render the gap between cost
 * and market value as the thing it is.
 *
 * Units with no purchase date cannot be placed in time. They are counted in
 * the final value (they are owned) but contribute no step, rather than being
 * dropped or dated to today — see the `undated` return.
 */
export function portfolioValueSeries(
  units: Property[],
  marketValueToday: number,
  locale = 'en-GB',
  now: Date = new Date(),
): { points: ValuePoint[]; undated: number; costBasis: number } {
  const buys = (units || [])
    .map((u) => ({
      at: u.purchaseDate || u.otpDate || u.createdAt,
      amount: Number(u.purchaseValue) || 0,
    }))
    .filter((b) => b.amount > 0);

  const dated = buys
    .map((b) => ({ ...b, t: b.at ? Date.parse(b.at) : NaN }))
    .filter((b) => !isNaN(b.t) && b.t <= now.getTime())
    .sort((a, b) => a.t - b.t);

  const costBasis = buys.reduce((s, b) => s + b.amount, 0);
  const undated = buys.length - dated.length;

  const short = (t: number) =>
    new Date(t).toLocaleDateString(locale, { month: 'short', year: '2-digit' });

  let running = 0;
  const points: ValuePoint[] = dated.map((b) => {
    running += b.amount;
    return {
      key: new Date(b.t).toISOString().slice(0, 10),
      label: short(b.t),
      value: running,
      today: false,
    };
  });

  // Today's valuation closes the line. Without a market value we still close
  // it at cost, so the chart ends on the day rather than on the last purchase.
  const end = marketValueToday > 0 ? marketValueToday : costBasis;
  if (points.length > 0 || end > 0) {
    points.push({
      key: now.toISOString().slice(0, 10),
      label: short(now.getTime()),
      value: end,
      today: true,
    });
  }

  return { points, undated, costBasis };
}

// ---------------------------------------------------------------- sales
/**
 * Dubai resale transaction costs — PREFILL DEFAULTS ONLY.
 *
 * Every one of these is editable in the sale form and stored as entered,
 * because what the owner actually paid is what the ROI has to be built on:
 * agency commission is negotiated deal by deal, and a developer NOC or a
 * mortgage discharge can dwarf the DLD fee.
 */
export const DLD_TRANSFER_RATE = 0.04;        // 4% DLD transfer fee
export const AGENCY_COMMISSION_RATE = 0.02;   // 2% agency commission
export const TRUSTEE_FEE_STANDARD = 4200;     // trustee office, sale ≥ AED 500k (4,000 + 5% VAT)
export const TRUSTEE_FEE_LOW = 2100;          // sale < AED 500k (2,000 + 5% VAT)
export const TRUSTEE_FEE_THRESHOLD = 500000;

export const trusteeFeeFor = (salePrice: number) =>
  salePrice >= TRUSTEE_FEE_THRESHOLD ? TRUSTEE_FEE_STANDARD : TRUSTEE_FEE_LOW;

/**
 * Two very different transactions.
 *
 * 'transfer'   — a completed unit with title. You own it outright, the buyer
 *                pays the full price, and your capital was the purchase price.
 * 'assignment' — an OFF-PLAN unit sold before handover. Title does not exist
 *                yet: what changes hands is the SPA. The buyer takes over the
 *                remaining installments and pays you the equity plus premium,
 *                so the cash you receive is the price MINUS the balance still
 *                owed to the developer — and your capital was only what you
 *                have actually paid in, never the full SPA price.
 *
 * Net profit works out the same either way (price − SPA price − costs), but
 * the ROI denominator does not, and on a 30%-paid off-plan unit that is the
 * difference between a 12% return and a 40% one.
 */
export type SaleType = 'transfer' | 'assignment';

/**
 * How the owner came to hold an off-plan unit — it decides what their capital was.
 *
 * 'developer'    — bought the SPA from the developer. Capital is simply the
 *                  installments paid in.
 * 'assigned-in'  — TOOK THE UNIT OVER FROM ANOTHER OWNER, i.e. bought an
 *                  assignment themselves. Capital is the price paid to that
 *                  owner (their equity plus whatever premium was agreed) PLUS
 *                  the installments paid to the developer since. The SPA price
 *                  on the record is then the developer's contract value, NOT
 *                  what this owner paid, so it must never be used as the base.
 */
export type AcquisitionRoute = 'developer' | 'assigned-in';

/** Off-plan resale costs the seller carries that a title transfer does not. */
export const NOC_FEE_TYPICAL_LOW = 500;    // developer NOC, low end
export const NOC_FEE_TYPICAL_HIGH = 5250;  // developer NOC, high end (incl. VAT)

export interface SaleExpense { label: string; amount: number }

export interface SaleCosts {
  dldFee?: number;
  commission?: number;
  trusteeFee?: number;
  /** developer No-Objection Certificate — mandatory to assign an off-plan SPA */
  nocFee?: number;
  /** developer administration / assignment fee, sometimes a % of the original price */
  developerAdminFee?: number;
  otherExpenses?: SaleExpense[];
  otherTotal?: number;
}

export interface SaleSummary {
  saleType: SaleType;
  salePrice: number;
  /** the SPA / contract price — what the unit was bought for */
  purchase: number;
  dldFee: number;
  commission: number;
  trusteeFee: number;
  nocFee: number;
  developerAdminFee: number;
  otherExpenses: SaleExpense[];
  otherTotal: number;
  totalCosts: number;
  /** sale price − transaction costs (still gross of any developer balance) */
  netProceeds: number;
  /** how the unit was acquired — decides what capitalBase is built from */
  acquisitionRoute: AcquisitionRoute;
  /**
   * What was paid to the PREVIOUS owner to take the SPA over. Zero when the
   * unit was bought from the developer. Counted in full as capital: it already
   * reimbursed that owner for the installments they had paid.
   */
  acquisitionPrice: number;
  /**
   * Assignment + assigned-in only: the balance still owed to the developer at
   * the moment this owner took the SPA over.
   *
   * The price on a DLD assignment is the whole value of the unit at that date,
   * and the unpaid balance came with it — so what actually reached the previous
   * owner was price − balance. Leaving this at zero charged the same developer
   * money twice, once inside the entry price and again when the exit balance
   * came off the proceeds, and turned a profitable deal into a large loss.
   */
  assumedBalance: number;
  /** acquisitionPrice − assumedBalance: the cash that actually reached the previous owner */
  acquisitionCash: number;
  /**
   * Installments this owner paid to the developer. On an 'assigned-in' unit
   * that is only the payments made SINCE taking over — the earlier ones were
   * the previous owner's and are already inside acquisitionPrice.
   * Equals `purchase` on a completed title transfer.
   */
  paidToDate: number;
  /** balance the buyer takes over on an assignment; 0 on a transfer */
  outstandingBalance: number;
  /** the money that actually reaches you: netProceeds − outstandingBalance */
  cashToSeller: number;
  /**
   * What it cost to ACQUIRE the unit, on top of the price: the DLD/Oqood 4%,
   * the agency's 2% and the trustee office fee. Real money out of pocket, so it
   * belongs in the capital the return is measured against. Mostly relevant to a
   * unit bought from another owner — a purchase straight from the developer
   * carries no agency commission and no trustee fee.
   */
  purchaseDldFee: number;
  purchaseCommission: number;
  purchaseTrusteeFee: number;
  purchaseOtherCosts: number;
  /** the four above, added up */
  purchaseCostsTotal: number;
  /** price + acquisition costs: everything this owner actually put in */
  totalInvested: number;
  /** the capital the return is measured against — equals totalInvested */
  capitalBase: number;
  /**
   * The price this owner bought at: the assignment price on a unit taken over
   * from another owner, the SPA price otherwise. Not always the SPA price — a
   * unit bought off a distressed seller was bought below it.
   */
  entryPrice: number;
  /** price movement alone, before any cost */
  grossProfit: number;
  /** cashToSeller − capitalBase. Identical on both routes; only the ROI base differs. */
  netProfit: number;
  /** netProfit ÷ capitalBase — return on the money actually put in */
  roiPercent: number;
  grossRoiPercent: number;
  /**
   * false until there is both a sale price and a capital base. Profit and ROI
   * are then undefined, NOT zero — an empty form would otherwise assert a
   * total loss of the purchase price, and netProfit on a missing basis would
   * report the whole proceeds as gain. Callers must print "—" and keep such a
   * sale out of portfolio profit totals.
   */
  hasBasis: boolean;
  holdingMonths: number | null;
  /** simple annualisation, only from 3 months of holding onward */
  annualizedRoi: number | null;
  saleDate?: string;
}

const saleNum = (v: any): number => {
  const n = typeof v === 'number' ? v : parseFloat(String(v ?? '').replace(/,/g, ''));
  return Number.isFinite(n) && n > 0 ? n : 0;
};

/**
 * The one place sale returns are computed. The backend mirrors this formula
 * when it stores a sale; the UI recomputes with it so the live preview in the
 * sale form, the sold-unit card and the report can never disagree — and so a
 * sale recorded by the DubaiSell webhook (which stores no cost breakdown)
 * still reports a consistent ROI, just with zero costs.
 */
export function computeSale(input: {
  salePrice: any;
  purchasePrice: any;
  purchaseDate?: string;
  saleDate?: string;
  dldFee?: any;
  commission?: any;
  trusteeFee?: any;
  nocFee?: any;
  developerAdminFee?: any;
  otherExpenses?: SaleExpense[];
  /** 'assignment' for an off-plan unit sold before handover — see SaleType. */
  saleType?: SaleType;
  /** assignment only: installments THIS owner paid to the developer. */
  paidToDate?: any;
  /** assignment only: balance the buyer takes over. Defaults to purchase − paidToDate. */
  outstandingBalance?: any;
  /** assignment only: how the unit was acquired. Defaults to 'developer'. */
  acquisitionRoute?: AcquisitionRoute;
  /** assignment only: price paid to the previous owner to take the SPA over. */
  acquisitionPrice?: any;
  /** assigned-in only: developer balance that came with the SPA at takeover. */
  assumedBalance?: any;
  /** what was paid to ACQUIRE the unit, over and above the price. */
  purchaseDldFee?: any;
  purchaseCommission?: any;
  purchaseTrusteeFee?: any;
  purchaseOtherCosts?: any;
}): SaleSummary {
  const otherExpenses = (input.otherExpenses || [])
    .map(e => ({ label: (e.label || '').trim() || 'Other expense', amount: saleNum(e.amount) }))
    .filter(e => e.amount > 0);
  const otherTotal = otherExpenses.reduce((s, e) => s + e.amount, 0);

  const salePrice = saleNum(input.salePrice);
  const purchase = saleNum(input.purchasePrice);
  const purchaseDldFee = saleNum(input.purchaseDldFee);
  const purchaseCommission = saleNum(input.purchaseCommission);
  const purchaseTrusteeFee = saleNum(input.purchaseTrusteeFee);
  const purchaseOtherCosts = saleNum(input.purchaseOtherCosts);
  const purchaseCostsTotal = purchaseDldFee + purchaseCommission + purchaseTrusteeFee + purchaseOtherCosts;
  const dldFee = saleNum(input.dldFee);
  const commission = saleNum(input.commission);
  const trusteeFee = saleNum(input.trusteeFee);
  const nocFee = saleNum(input.nocFee);
  const developerAdminFee = saleNum(input.developerAdminFee);

  const totalCosts = dldFee + commission + trusteeFee + nocFee + developerAdminFee + otherTotal;
  const netProceeds = salePrice - totalCosts;

  const saleType: SaleType = input.saleType === 'assignment' ? 'assignment' : 'transfer';

  // On a transfer the unit is fully yours: capital = purchase price, nothing owed.
  // On an assignment the balance still owed to the developer leaves with the
  // buyer, and the capital is whatever THIS owner actually put in.
  const acquisitionRoute: AcquisitionRoute =
    saleType === 'assignment' && input.acquisitionRoute === 'assigned-in' ? 'assigned-in' : 'developer';
  // No clamp to the SPA price here. A unit taken over from another owner can
  // legitimately have cost more in total than the developer's contract value —
  // the premium paid to that owner sits on top of it.
  const paidToDate = saleType === 'assignment' ? saleNum(input.paidToDate) : purchase;
  const acquisitionPrice = saleType === 'assignment' && acquisitionRoute === 'assigned-in'
    ? saleNum(input.acquisitionPrice)
    : 0;
  // A DLD assignment price is the whole value of the unit at that date, and the
  // developer's unpaid balance transferred with it. Only the difference ever
  // left this owner's pocket; the balance was paid off later, installment by
  // installment, and those installments are counted separately in paidToDate.
  const assumedBalance = saleType === 'assignment' && acquisitionRoute === 'assigned-in'
    ? saleNum(input.assumedBalance)
    : 0;
  const acquisitionCash = Math.max(0, acquisitionPrice - assumedBalance);

  const outstandingBalance = saleType === 'assignment'
    ? (input.outstandingBalance == null
        ? (acquisitionRoute === 'assigned-in'
            // On a unit taken over from someone else the schedule was already
            // part-paid before this owner arrived, so the SPA price says
            // nothing about what is left. What is left is the balance they
            // took over, less the installments they have paid since. Deriving
            // it from the SPA price instead counted the previous owner's
            // payments as still outstanding.
            ? Math.max(0, assumedBalance - paidToDate)
            // Straight from the developer: everything paid against the SPA was
            // paid by this owner, so the balance is the rest of the price.
            : Math.max(0, purchase - paidToDate))
        : Math.max(0, saleNum(input.outstandingBalance)))
    : 0;

  const cashToSeller = netProceeds - outstandingBalance;
  // Acquisition costs are capital too: money this owner paid out to come to
  // hold the unit, never recovered on the way out. Leaving them out reported a
  // return on less capital than was actually at risk.
  const priceIn = saleType === 'assignment' ? acquisitionCash + paidToDate : purchase;
  const totalInvested = priceIn + purchaseCostsTotal;
  const capitalBase = totalInvested;

  // Measured against what this owner bought at. On an assigned-in unit the SPA
  // price is the developer's contract value, not the price paid to take it
  // over — comparing to it reported a price fall on a unit bought at a discount.
  const entryPrice = acquisitionPrice > 0 ? acquisitionPrice : purchase;
  const grossProfit = salePrice - entryPrice;
  // cashToSeller − capitalBase. On an assignment that expands to
  // salePrice − costs − outstanding − paid = salePrice − costs − purchase,
  // i.e. the same profit as a transfer — only the ROI base changes.
  const netProfit = cashToSeller - capitalBase;

  const purchaseAt = input.purchaseDate ? new Date(input.purchaseDate).getTime() : NaN;
  const saleAt = input.saleDate ? new Date(input.saleDate).getTime() : Date.now();
  const holdingMonths = !isNaN(purchaseAt) && !isNaN(saleAt) && saleAt > purchaseAt
    ? Math.max(1, Math.round((saleAt - purchaseAt) / (86400e3 * 30.44)))
    : null;

  // A price of zero means "not entered yet", not "sold for nothing". Reporting
  // −100% against an empty form was asserting a total loss before the owner
  // had typed anything.
  const hasBasis = salePrice > 0 && capitalBase > 0;
  const roiPercent = hasBasis ? (netProfit / capitalBase) * 100 : 0;

  return {
    saleType,
    salePrice,
    purchase,
    dldFee,
    commission,
    trusteeFee,
    nocFee,
    developerAdminFee,
    otherExpenses,
    otherTotal,
    totalCosts,
    netProceeds,
    acquisitionRoute,
    acquisitionPrice,
    assumedBalance,
    acquisitionCash,
    paidToDate,
    outstandingBalance,
    cashToSeller,
    purchaseDldFee,
    purchaseCommission,
    purchaseTrusteeFee,
    purchaseOtherCosts,
    purchaseCostsTotal,
    totalInvested,
    capitalBase,
    entryPrice,
    grossProfit,
    netProfit,
    roiPercent,
    grossRoiPercent: hasBasis && entryPrice > 0 ? (grossProfit / entryPrice) * 100 : 0,
    hasBasis,
    holdingMonths,
    // Under three months an annualisation says more about the calendar than
    // the deal, so it is left off rather than printed as a 400% return.
    annualizedRoi: hasBasis && holdingMonths && holdingMonths >= 3
      ? roiPercent * (12 / holdingMonths)
      : null,
    saleDate: input.saleDate
  };
}

/** Completion % from the DLD construction feed (Mashrooi), or null when no project matched. */
export function dldConstructionPercent(unit: Property): number | null {
  const p = unit.constructionCompletionPercent;
  return typeof p === 'number' && isFinite(p) ? p : null;
}

/**
 * Off-plan means no title yet, so a sale is an assignment of the SPA.
 * Same test buildRows uses for the portfolio split, minus the sold filter:
 * an explicit Offplan status, or a handover date still in the future with
 * construction not finished.
 */
export function isOffplanUnit(unit: Property): boolean {
  // The DLD construction feed decides when it knows the project: the stored
  // occupancyStatus is set once at upload (often 'Vacant' for an off-plan
  // unit) and never follows the build, so Address Residences at 26% read
  // "Vacant" and the finished Royal Atlantis read "Im Bau" (2026-11-04).
  const live = dldConstructionPercent(unit);
  if (live !== null) return live < 100;
  if ((unit.occupancyStatus || '').toLowerCase() === 'offplan') return true;
  const handoverISO = unit.completionDate || unit.estimatedCompletionDate;
  if (!handoverISO) return false;
  const handoverAt = new Date(handoverISO).getTime();
  if (isNaN(handoverAt)) return false;
  return handoverAt > Date.now() && (unit.projectProgress ?? 0) < 100;
}

/**
 * Installments paid in and still owed, from a payment plan.
 *
 * Milestone status alone cannot be trusted: an SPA carries the schedule but no
 * paid/due columns, so every SPA-sourced milestone stays 'pending' forever —
 * including installments settled years ago. The owner's rule, already applied
 * to the live plans, is that a milestone due before today counts as PAID,
 * because developers do not reissue the SOA; only today-or-later is genuinely
 * open. Same principle as chequeCollected above.
 */
export function planPaidToDate(plan: { milestones?: Milestone[] } | null | undefined) {
  const milestones = plan?.milestones || [];
  let paid = 0, open = 0, total = 0;
  milestones.forEach(m => {
    const amount = m.amount || 0;
    total += amount;
    const settled = m.status === 'paid'
      || (!!m.dueDate && new Date(m.dueDate).getTime() <= Date.now());
    if (settled) paid += amount; else open += amount;
  });
  return { paid, open, total, hasPlan: milestones.length > 0 };
}

export const isSold = (unit: Property) => (unit.status || '').toLowerCase() === 'sold';

/** The sale summary for a sold unit, recomputed from what was stored. */
export function summariseSale(unit: Property): SaleSummary | null {
  if (!isSold(unit)) return null;
  const costs = unit.saleCosts || {};
  return computeSale({
    salePrice: unit.salePrice,
    purchasePrice: unit.purchaseValue,
    purchaseDate: unit.purchaseDate || unit.otpDate || unit.createdAt,
    saleDate: unit.saleDate || unit.soldAt,
    dldFee: costs.dldFee,
    commission: costs.commission,
    trusteeFee: costs.trusteeFee,
    nocFee: costs.nocFee,
    developerAdminFee: costs.developerAdminFee,
    otherExpenses: costs.otherExpenses,
    // Sales recorded before the off-plan route existed carry no saleType and
    // replay as transfers, which is what they were priced as.
    saleType: unit.saleType,
    paidToDate: unit.paidToDate,
    outstandingBalance: unit.outstandingBalance,
    acquisitionRoute: unit.acquisitionRoute,
    acquisitionPrice: unit.acquisitionPrice,
    // Sales recorded before the assumed balance existed stored none, and
    // replay exactly as they were priced.
    assumedBalance: unit.assumedBalance,
    // Stored on the unit by the backend. A sale recorded before acquisition
    // costs existed has none, and replays exactly as it was priced.
    purchaseDldFee: unit.purchaseCosts?.purchaseDldFee,
    purchaseCommission: unit.purchaseCosts?.purchaseCommission,
    purchaseTrusteeFee: unit.purchaseCosts?.purchaseTrusteeFee,
    purchaseOtherCosts: unit.purchaseCosts?.purchaseOtherCosts
  });
}

export interface SoldRow { unit: Property; sale: SaleSummary }

/** One row per sold unit, most recent sale first. */
export function buildSoldRows(units: Property[]): SoldRow[] {
  return (units || [])
    .filter(isSold)
    .map(unit => ({ unit, sale: summariseSale(unit)! }))
    .sort((a, b) =>
      new Date(b.sale.saleDate || 0).getTime() - new Date(a.sale.saleDate || 0).getTime());
}

/**
 * Portfolio-level realised totals across every sold unit.
 *
 * Proceeds and costs cover every sale; profit and ROI cover only the sales
 * with a purchase price on record, because a sale with no cost basis has no
 * knowable profit — counting its proceeds as gain would overstate the
 * realised return. `unpriced` reports how many were left out.
 */
export function totalRealised(soldRows: SoldRow[]) {
  const sum = (pick: (s: SaleSummary) => number, from = soldRows) =>
    from.reduce((s, r) => s + pick(r.sale), 0);
  const priced = soldRows.filter(r => r.sale.hasBasis);
  // Capital base, not purchase price: an off-plan assignment only ever tied up
  // the installments actually paid, so summing SPA prices would understate the
  // portfolio's realised return on the money that was really at work.
  const invested = sum(s => s.capitalBase, priced);
  const netProfit = sum(s => s.netProfit, priced);
  return {
    count: soldRows.length,
    unpriced: soldRows.length - priced.length,
    assignments: soldRows.filter(r => r.sale.saleType === 'assignment').length,
    invested,
    salePrice: sum(s => s.salePrice),
    totalCosts: sum(s => s.totalCosts),
    netProceeds: sum(s => s.netProceeds),
    /** what actually reached the owner, after any developer balance left with the buyer */
    cashToSeller: sum(s => s.cashToSeller),
    outstandingTransferred: sum(s => s.outstandingBalance),
    grossProfit: sum(s => s.grossProfit, priced),
    netProfit,
    roiPercent: invested > 0 ? (netProfit / invested) * 100 : 0
  };
}

// ---------------------------------------------------------------- rows
/** One enriched row per unit, largest purchase first. */
export function buildRows(
  units: Property[],
  dldData: any,
  leases: Record<string, LeaseInfo>
): PortfolioRow[] {
  // same matching rules as the Valuation tab: unit id first, then project name
  const dldUnitFor = (unit: Property) => {
    const list = dldData?.units || [];
    const byId = list.find((u: any) => u.unitId === unit.id);
    if (byId?.comparablesCount > 0) return byId;
    const project = (unit.project || '').toLowerCase().trim();
    if (project) {
      const byProject = list.find((u: any) => {
        const p = (u.project || '').toLowerCase().trim();
        return p && (p === project || p.includes(project) || project.includes(p));
      });
      if (byProject?.comparablesCount > 0) return byProject;
    }
    return byId || null;
  };

  // A sold unit is no longer a holding: it must not be counted in total
  // investment, market value or yield. Its money now shows up as realised
  // proceeds in the sales section — filtered here so every consumer of
  // buildRows (report, revenue, calculator) drops it in step.
  return units.filter(unit => !isSold(unit)).map(unit => {
    const dld = dldUnitFor(unit);
    const lease = leases[unit.id];
    const purchase = unit.purchaseValue || 0;
    const handoverISO = unit.completionDate || unit.estimatedCompletionDate;
    const offplan = isOffplanUnit(unit);
    // Rental income comes ONLY from a signed Property Management lease (0 if none) —
    // no estimated/market rent, so "rented" reflects actual leases.
    const annualRent = lease?.annualRent || 0;
    const rented = annualRent > 0
      || ['occupied', 'rented'].includes((unit.occupancyStatus || '').toLowerCase())
      || (lease?.status || '').toLowerCase() === 'rented';
    return {
      unit,
      dld,
      lease,
      purchase,
      // NEVER synthesise a market value. Either DLD can value the unit from real
      // comparable sales, or we fall back to what was actually paid — no 8%/year
      // appreciation guesses, no stale book values. Keeps the Report, Valuation
      // and Performance tabs telling the same story about the same unit.
      marketValue: dld?.dldMarketValue || purchase,
      hasDldValue: !!dld?.dldMarketValue,
      offplan,
      rented,
      monthlyRent: annualRent / 12,
      estMonthlyRent: dld?.dldAvgMonthlyRent || 0,
      annualRent,
      handoverISO,
      handoverLabel: offplan ? quarterLabel(handoverISO) : '—',
      area: toSqft(unit.area)   // always sqft — raw values may be sqm
    };
  }).sort((a, b) => b.purchase - a.purchase);
}

// ------------------------------------------------------------ live counters
export interface ValueGrowth {
  /** Today's DLD valuation of the held units (fallback: purchase price). */
  valueToday: number;
  /** Purchase prices of the same units. */
  invested: number;
  /** Estimated annual growth, e.g. 0.016 for 1.6%. */
  rate: number;
  /** Units the rate was measured on (high/medium-confidence valuations). */
  basisUnits: number;
  /** When valueToday was valued — the ticker grows from here. */
  asOf: number;
  /** The working, for the "how this is calculated" panel. */
  units: number;
  basisCost: number;
  basisValue: number;
  /** Cost-weighted average years held, over the basis units. */
  years: number;
  excludedLow: number;
  excludedNoDate: number;
}

/**
 * An ESTIMATED growth rate for the portfolio's value, from the owner's own
 * DLD valuations against what they paid — never a market assumption.
 *
 * DLD publishes a valuation for today, not a price history, so the only
 * growth on record is purchase price -> today's valuation, per unit. The rate
 * is the portfolio's annualised growth over that span: total value / total
 * cost, to the power of 1 / the cost-weighted years held.
 *
 * LOW-confidence valuations are left out of the RATE (they stay in the value):
 * one thinly-traded building valued at twice its price would otherwise set the
 * growth of the whole portfolio. Returns null when the measurement is too thin
 * to stand behind — fewer than 3 units, or under a year held on average,
 * because annualising a few months' move turns noise into a rate.
 */
/** One held unit in the portfolio value: its DLD value, or its purchase
 *  price when DLD has no valuation for it. */
export interface PortfolioValueRow {
  unit: any;
  value: number;
  source: 'dld' | 'purchase';
  confidence: string | null;
  /** DLD market rent for this unit (registered rent contracts), if known. */
  marketRent: number;
}

/**
 * THE portfolio value — the one rule every screen uses, so Home and
 * Properties can never show two different numbers for the same portfolio
 * (they did: 45.2M on Home, 49.7M on Properties, 2026-10-09).
 * Held units with a purchase price; each at its DLD valuation, or at its
 * purchase price when DLD has none.
 */
export function portfolioDldValue(dld: any, units: any[]): { value: number; invested: number; rows: PortfolioValueRow[] } {
  const byId = new Map<string, any>();
  (dld?.units || []).forEach((x: any) => { if (x?.unitId) byId.set(String(x.unitId), x); });
  const rows: PortfolioValueRow[] = [];
  let value = 0, invested = 0;
  (units || []).forEach((u: any) => {
    if ((u?.status || '').toLowerCase() === 'sold') return;
    const pv = Number(u?.purchaseValue) || 0;
    if (!(pv > 0)) return;
    const x = byId.get(String(u.id));
    const mv = Number(x?.dldMarketValue) || 0;
    const v = mv > 0 ? mv : pv;
    invested += pv;
    value += v;
    rows.push({ unit: u, value: v, source: mv > 0 ? 'dld' : 'purchase', confidence: x?.confidence ? String(x.confidence) : null, marketRent: Number(x?.dldAvgAnnualRent) || 0 });
  });
  return { value, invested, rows };
}

/**
 * Rental income that is REAL: a signed lease, or rent recorded on the unit.
 * Never a percentage of the value — the app used to fill every unit without
 * rent with 5% of its value a year, which made 2.57M of "rental income" out
 * of three leases. A vacant unit earns nothing; what it COULD earn at DLD
 * market rent is reported separately as potential, never added to income.
 */
export function portfolioRent(dld: any, units: any[], leases: Record<string, LeaseInfo> = {}) {
  const { rows } = portfolioDldValue(dld, units);
  let actual = 0, letValue = 0, potential = 0, letCount = 0, vacantWithMarket = 0;
  const lines = rows.map((r) => {
    const u = r.unit;
    // A signed lease: from Property Management (the same source as Revenue &
    // return), or on the unit record itself.
    const lease = Number(leases[String(u?.id)]?.annualRent) || Number(u?.currentTenant?.annualRent) || 0;
    const estimated = String(u?.rentalDataSource || '').toLowerCase().startsWith('estimated');
    const onRecord = !estimated ? (Number(u?.monthlyRental) || 0) * 12 : 0;
    const rent = lease > 0 ? lease : onRecord;
    if (rent > 0) { actual += rent; letValue += r.value; letCount++; }
    else if (r.marketRent > 0) { potential += r.marketRent; vacantWithMarket++; }
    return { ...r, rent, rentSource: lease > 0 ? 'lease' as const : rent > 0 ? 'record' as const : 'none' as const };
  });
  // A signed lease counts as income even when the unit has no purchase price
  // on file (it is then outside the value rows, and outside the yield).
  const inRows = new Set(rows.map((r) => String(r.unit?.id)));
  (units || []).forEach((u: any) => {
    if ((u?.status || '').toLowerCase() === 'sold' || inRows.has(String(u?.id))) return;
    const lease = Number(leases[String(u?.id)]?.annualRent) || 0;
    if (lease > 0) { actual += lease; letCount++; }
  });
  return {
    actual, potential, letCount, vacantWithMarket, lines,
    /** Gross yield of the units that are actually let, on their own value. */
    letYield: letValue > 0 ? (actual / letValue) * 100 : 0,
  };
}

export function dldValueGrowth(dld: any, units: any[], now: Date = new Date()): ValueGrowth | null {
  const byId = new Map<string, any>();
  (dld?.units || []).forEach((x: any) => { if (x?.unitId) byId.set(String(x.unitId), x); });
  if (!byId.size) return null;

  const held = (units || []).filter((u: any) =>
    (u?.status || '').toLowerCase() !== 'sold' && (Number(u?.purchaseValue) || 0) > 0);

  // Value and invested from the shared rule above — the same numbers the
  // Properties screen shows.
  const totals = portfolioDldValue(dld, units);
  const valueToday = totals.value, invested = totals.invested;
  let cost = 0, value = 0, weighted = 0, n = 0, low = 0, noDate = 0;
  held.forEach((u: any) => {
    const pv = Number(u.purchaseValue) || 0;
    const x = byId.get(String(u.id));
    const mv = Number(x?.dldMarketValue) || 0;

    const conf = String(x?.confidence || '').toUpperCase();
    const at = Date.parse(u.purchaseDate || u.otpDate || '');
    if (!(mv > 0)) return;
    if (conf === 'LOW' || conf === 'INSUFFICIENT_DATA') { low++; return; }
    if (isNaN(at)) { noDate++; return; }
    const years = (now.getTime() - at) / (365.25 * 86400000);
    if (years <= 0) return;
    cost += pv; value += mv; weighted += pv * years; n++;
  });

  if (n < 3 || cost <= 0) return null;
  const years = weighted / cost;
  if (years < 1) return null;
  const rate = Math.pow(value / cost, 1 / years) - 1;
  const synced = Date.parse(dld?.lastSync || '');
  return {
    valueToday, invested, rate, basisUnits: n, asOf: isNaN(synced) ? now.getTime() : synced,
    units: held.length, basisCost: cost, basisValue: value, years, excludedLow: low, excludedNoDate: noDate,
  };
}

/**
 * Rent accruing from the signed leases, for the live rent counter.
 *
 * `perSecond` is every current lease's annual rent spread evenly over the
 * year; `earnedThisYear` is the part of each lease's rent that falls between
 * 1 January and `now` (day-exact, from contractStart/contractEnd). Contract
 * rent, not a forecast: a unit with no signed tenancy contributes nothing.
 */
export function rentAccrual(units: any[], now: Date = new Date()) {
  const DAY = 86400000;
  const day = (raw: any) => {
    const d = typeof raw === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(raw)
      ? new Date(+raw.slice(0, 4), +raw.slice(5, 7) - 1, +raw.slice(8, 10)).getTime()
      : raw ? Date.parse(raw) : NaN;
    return isNaN(d) ? null : d;
  };
  const yearStart = new Date(now.getFullYear(), 0, 1).getTime();
  let perYear = 0;
  let earnedThisYear = 0;
  let leases = 0;
  (units || []).forEach((u: any) => {
    const tn = u?.currentTenant;
    const rent = Number(tn?.annualRent) || 0;
    if (!tn || !rent) return;
    const s = day(tn.contractStart);
    const e = day(tn.contractEnd);
    const end = e !== null ? e + DAY : null;          // contractEnd is inclusive
    if (end !== null && end <= now.getTime()) return; // lease over
    leases++;
    perYear += rent;
    const from = Math.max(yearStart, s ?? yearStart);
    const to = Math.min(now.getTime(), end ?? now.getTime());
    if (to > from) earnedThisYear += rent * ((to - from) / DAY) / 365;
  });
  return { earnedThisYear, perSecond: perYear / (365 * DAY / 1000), perDay: perYear / 365, leases, asOf: now.getTime() };
}
